import type { CellType, CellTypesConfig } from "./types";
import {
  DEFAULT_ARG_TYPES,
  defaultCatalogAsEntries,
  type CatalogMap,
  type FunctionCatalogEntry,
} from "./catalog";

/**
 * Cell-type rules and weights persistence. Same approach as
 * `src/rules/storage.ts`: localStorage-backed, per-user/per-device. New
 * keys (no migration from OfficeRuntime since this feature is new).
 */

const KEY_WEIGHTS = "cogseven.toolkit.celltypes.weights";
const KEY_DIRECT_RULES = "cogseven.toolkit.celltypes.directRules";
const KEY_CATALOG = "cogseven.toolkit.celltypes.catalog";

export interface ConfidenceWeights {
  high: number;
  medium: number;
  low: number;
}

export interface DirectRulesConfig {
  valueType: boolean;
  numberFormat: boolean;
  textPattern: boolean;
  valueRange: boolean;
}

export const DEFAULT_WEIGHTS: ConfidenceWeights = { high: 3, medium: 2, low: 1 };

export const DEFAULT_DIRECT_RULES: DirectRulesConfig = {
  valueType: true,
  numberFormat: true,
  textPattern: true,
  valueRange: true,
};

const VALID_TYPES: ReadonlySet<CellType> = new Set<CellType>([
  "date",
  "datetime",
  "time",
  "number",
  "integer",
  "percent",
  "currency",
  "boolean",
  "text",
  "empty",
  "error",
  "unknown",
]);

// ============================================================================
// Async helpers (use from dialog / scanner flow)
// ============================================================================

export async function loadWeights(): Promise<ConfidenceWeights> {
  return readWeights();
}

export async function saveWeights(w: ConfidenceWeights): Promise<void> {
  localStorage.setItem(KEY_WEIGHTS, JSON.stringify(sanitizeWeights(w)));
}

export async function resetWeights(): Promise<void> {
  localStorage.removeItem(KEY_WEIGHTS);
}

export async function loadDirectRules(): Promise<DirectRulesConfig> {
  return readDirectRules();
}

export async function saveDirectRules(d: DirectRulesConfig): Promise<void> {
  localStorage.setItem(KEY_DIRECT_RULES, JSON.stringify(sanitizeDirectRules(d)));
}

export async function resetDirectRules(): Promise<void> {
  localStorage.removeItem(KEY_DIRECT_RULES);
}

export async function loadCatalogEntries(): Promise<FunctionCatalogEntry[]> {
  return readCatalogEntries();
}

export async function saveCatalogEntries(entries: FunctionCatalogEntry[]): Promise<void> {
  localStorage.setItem(KEY_CATALOG, JSON.stringify(sanitizeCatalogEntries(entries)));
}

export async function resetCatalog(): Promise<void> {
  localStorage.removeItem(KEY_CATALOG);
}

/** Load full config — used by the scanner and the dialog init. */
export async function loadCellTypesConfig(): Promise<CellTypesConfig> {
  return loadCellTypesConfigSync();
}

// ============================================================================
// Sync helper (use from the audit check, which has a synchronous signature)
// ============================================================================

export function loadCellTypesConfigSync(): CellTypesConfig {
  return {
    weights: readWeights(),
    directRules: readDirectRules(),
    catalog: catalogMapFromEntries(readCatalogEntries()),
  };
}

// ============================================================================
// Internal readers + sanitization
// ============================================================================

function readWeights(): ConfidenceWeights {
  const raw = localStorage.getItem(KEY_WEIGHTS);
  if (!raw) return { ...DEFAULT_WEIGHTS };
  try {
    const parsed = JSON.parse(raw) as Partial<ConfidenceWeights>;
    return sanitizeWeights(parsed);
  } catch {
    return { ...DEFAULT_WEIGHTS };
  }
}

function sanitizeWeights(w: Partial<ConfidenceWeights>): ConfidenceWeights {
  const num = (v: unknown, fallback: number): number =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : fallback;
  return {
    high: num(w.high, DEFAULT_WEIGHTS.high),
    medium: num(w.medium, DEFAULT_WEIGHTS.medium),
    low: num(w.low, DEFAULT_WEIGHTS.low),
  };
}

function readDirectRules(): DirectRulesConfig {
  const raw = localStorage.getItem(KEY_DIRECT_RULES);
  if (!raw) return { ...DEFAULT_DIRECT_RULES };
  try {
    const parsed = JSON.parse(raw) as Partial<DirectRulesConfig>;
    return sanitizeDirectRules(parsed);
  } catch {
    return { ...DEFAULT_DIRECT_RULES };
  }
}

function sanitizeDirectRules(d: Partial<DirectRulesConfig>): DirectRulesConfig {
  const bool = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);
  return {
    valueType: bool(d.valueType, DEFAULT_DIRECT_RULES.valueType),
    numberFormat: bool(d.numberFormat, DEFAULT_DIRECT_RULES.numberFormat),
    textPattern: bool(d.textPattern, DEFAULT_DIRECT_RULES.textPattern),
    valueRange: bool(d.valueRange, DEFAULT_DIRECT_RULES.valueRange),
  };
}

function readCatalogEntries(): FunctionCatalogEntry[] {
  const raw = localStorage.getItem(KEY_CATALOG);
  if (!raw) return defaultCatalogAsEntries();
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return defaultCatalogAsEntries();
    const sanitized = sanitizeCatalogEntries(parsed as FunctionCatalogEntry[]);
    // Empty stored catalog (or one whose every entry failed sanitization)
    // falls back to defaults. An empty inference catalog would silently
    // disable every usage-based signal — almost certainly not what the user
    // wants. To genuinely disable usage inference, turn off rules at the
    // detector level instead.
    if (sanitized.length === 0) return defaultCatalogAsEntries();
    return sanitized;
  } catch {
    return defaultCatalogAsEntries();
  }
}

function sanitizeCatalogEntries(entries: FunctionCatalogEntry[]): FunctionCatalogEntry[] {
  const out: FunctionCatalogEntry[] = [];
  const seen = new Set<string>();
  for (const raw of entries) {
    if (!raw || typeof raw !== "object") continue;
    const name = typeof raw.name === "string" ? raw.name.trim().toUpperCase() : "";
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const typesArr = Array.isArray(raw.types) ? raw.types : [];
    const types: CellType[] = typesArr
      .map((t) => (typeof t === "string" ? (t.trim().toLowerCase() as CellType) : null))
      .filter((t): t is CellType => t !== null && VALID_TYPES.has(t));
    const variadic = typeof raw.variadic === "boolean" ? raw.variadic : false;
    out.push({ name, types, variadic });
  }
  return out;
}

function catalogMapFromEntries(entries: FunctionCatalogEntry[]): CatalogMap {
  const map: CatalogMap = new Map();
  for (const e of entries) {
    map.set(e.name, { types: e.types, variadic: e.variadic });
  }
  return map;
}

// Re-export so callers can grab defaults without importing both modules.
export { DEFAULT_ARG_TYPES, defaultCatalogAsEntries };
export type { FunctionCatalogEntry, CatalogMap };
